import { Copy, Plus, Search, Trash2 } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAdminAuth } from '../auth/AdminAuthProvider'
import { adminCapabilities } from '../auth/permissions'
import { duplicateAnnouncement, fetchAnnouncements, formatDateTime, removeAnnouncement } from '../lib/data'

const STATUS_LABELS = { all: '전체 상태', draft: '초안', scheduled: '예약', published: '게시 중', ended: '종료' }
const PLACEMENT_LABELS = { modal: '모달', bottomSheet: '바텀시트', banner: '배너' }

export default function AnnouncementsPage() {
  const auth = useAdminAuth()
  const { canWrite } = adminCapabilities(auth.role)
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const search = params.get('q') || ''
  const status = params.get('status') || 'all'

  async function reload() {
    setLoading(true); setError('')
    try { setItems(await fetchAnnouncements()) } catch (err) { setError(err.message) } finally { setLoading(false) }
  }
  useEffect(() => { reload() }, [])
  const filtered = useMemo(() => items.filter(item => (status === 'all' || item.status === status) && (!search || `${item.title} ${item.description}`.toLowerCase().includes(search.toLowerCase()))), [items, search, status])

  function setFilter(key, value) {
    const next = new URLSearchParams(params)
    value ? next.set(key, value) : next.delete(key)
    setParams(next, { replace: true })
  }

  async function duplicate(event, item) {
    event.stopPropagation()
    try { const id = await duplicateAnnouncement(item, auth.user.uid); navigate(`/announcements/${id}`) } catch (err) { setError(err.message) }
  }
  async function remove(event, item) {
    event.stopPropagation()
    if (!window.confirm(`“${item.title}” 안내를 삭제할까요? 이 작업은 되돌릴 수 없습니다.`)) return
    try { await removeAnnouncement(item, auth.user.uid); setItems(current => current.filter(candidate => candidate.id !== item.id)) } catch (err) { setError(err.message) }
  }

  return (
    <>
      <header className="page-header"><div><p className="eyebrow">콘텐츠</p><h1>앱 안내</h1><p>{canWrite ? '사용자에게 보여 줄 안내를 작성하고 게시 일정을 관리합니다.' : '현재 안내와 게시 상태를 읽기 전용으로 확인합니다.'}</p></div>{canWrite && <div className="header-actions"><Link className="button primary" to="/announcements/new"><Plus size={17} />새 안내</Link></div>}</header>
      {error && <div className="error-state">{error}</div>}
      <div className="toolbar">
        <label className="search-field"><Search size={17} /><input className="input" aria-label="안내 검색" placeholder="제목 또는 설명 검색" value={search} onChange={event => setFilter('q', event.target.value)} /></label>
        <select className="select" aria-label="상태 필터" style={{ width: 160 }} value={status} onChange={event => setFilter('status', event.target.value)}>{Object.entries(STATUS_LABELS).map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select>
      </div>
      <section className="panel table-wrap">
        {loading ? <div className="empty-state">안내 목록을 불러오는 중…</div> : filtered.length === 0 ? <div className="empty-state">조건에 맞는 안내가 없습니다.</div> : (
          <table className="data-table"><thead><tr><th>안내</th><th>상태</th><th>위치</th><th>우선순위</th><th>수정일</th>{canWrite && <th>작업</th>}</tr></thead><tbody>{filtered.map(item => (
            <tr key={item.id} onClick={() => navigate(`/announcements/${item.id}`)}>
              <td className="cell-title"><strong>{item.title}</strong><small>{item.description}</small></td>
              <td data-label="상태"><span className={`status-badge ${item.status}`}>{STATUS_LABELS[item.status] || item.status}</span></td>
              <td data-label="위치">{PLACEMENT_LABELS[item.placement] || item.placement}</td><td data-label="우선순위">{item.priority ?? 0}</td><td data-label="수정일">{formatDateTime(item.updatedAt)}</td>
              {canWrite && <td data-label="작업"><button className="icon-button" type="button" onClick={event => duplicate(event, item)} aria-label="복제"><Copy size={16} /></button><button className="icon-button" type="button" onClick={event => remove(event, item)} aria-label="삭제"><Trash2 size={16} /></button></td>}
            </tr>))}</tbody></table>
        )}
      </section>
    </>
  )
}
